/*
 * Loaded globally from snippets/scripts.liquid: the offer card is often inserted by a drawer
 * re-render (morph), and scripts inserted that way never execute.
 * '@shopify/events' is imported lazily so a failure there can't stop the element from registering.
 */
let eventsModule = null;
async function loadEvents() {
  if (eventsModule) return eventsModule;
  try {
    eventsModule = await import('@shopify/events');
  } catch (error) {
    console.warn('[cart-upgrade] @shopify/events unavailable', error);
    eventsModule = {};
  }
  return eventsModule;
}

const routesRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

async function postJSON(path, body) {
  const response = await fetch(`${routesRoot()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  let data = null;
  try {
    data = await response.json();
  } catch (error) {
    data = null;
  }
  if (!response.ok || !data || data.status || data.errors) {
    const message = (data && (data.description || data.message || data.errors)) || `Request failed: ${response.status}`;
    throw new Error(typeof message === 'string' ? message : 'Cart request failed');
  }
  return data;
}

async function fetchCart() {
  const response = await fetch(`${routesRoot()}cart.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

/**
 * Fallback drawer refresh, the same calls cart-items-component makes: morph the section HTML
 * returned by the cart request, else re-render the section; reload only as a last resort.
 * @param {string[]} sectionIds
 * @param {Record<string, string>} [sections]
 */
async function refreshDrawer(sectionIds, sections) {
  try {
    const { morphSection, sectionRenderer } = await import('@theme/section-renderer');
    for (const id of sectionIds) {
      const html = sections?.[id];
      if (html) {
        await morphSection(id, html, { mode: 'hydration' });
      } else {
        await sectionRenderer.renderSection(id, { cache: false, mode: 'hydration' });
      }
    }
  } catch (error) {
    console.warn('[cart-upgrade] drawer refresh failed, reloading', error);
    window.location.reload();
  }
}

/**
 * Cart drawer upgrade offer: swaps one unit of a bundle line for the upgrade product.
 * Adds the upgrade first, so a failed add leaves the cart untouched, then lowers the
 * bundle line by one and lets the cart drawer re-render from the returned sections.
 */
class CartUpgradeOffer extends HTMLElement {
  #busy = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-upgrade-button]');
    if (!(button instanceof HTMLButtonElement) || button.disabled || this.#busy) return;
    event.preventDefault();
    this.#upgrade(button);
  };

  async #upgrade(button) {
    const variantId = Number(this.dataset.variantId);
    const lineKey = this.dataset.lineKey;
    const lineQuantity = Number(this.dataset.lineQuantity) || 1;
    if (!variantId || !lineKey) return;

    const errorElement = this.querySelector('[data-upgrade-error]');
    errorElement?.classList.add('hidden');

    this.#busy = true;
    const label = button.textContent;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Upgrading…';

    const sectionIds = [
      ...new Set(
        Array.from(document.querySelectorAll('cart-items-component'))
          .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
          .filter(Boolean)
      ),
    ];

    const properties = {};
    const includes = (this.dataset.includes || '').trim();
    if (includes) properties[this.dataset.includesLabel || 'Includes'] = includes;
    const band = (this.dataset.band || '').trim();
    if (band) properties[this.dataset.bandLabel || 'Band'] = band;

    // Same event the theme's own cart buttons dispatch; cart-items-component re-renders the
    // drawer from the sections we resolve it with. If it can't be dispatched, refresh ourselves.
    const { CartLinesUpdateEvent, CartErrorEvent } = await loadEvents();
    let deferred = null;
    try {
      deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'cart',
          lines: [{ merchandiseId: String(variantId), quantity: 1 }],
          promise: deferred.promise,
        })
      );
    } catch (error) {
      console.warn('[cart-upgrade] cart event not dispatched, using fallback refresh', error);
      deferred = null;
    }
    const toCart = (cart) => {
      try {
        return CartLinesUpdateEvent.createCartFromAjaxResponse(cart);
      } catch (error) {
        return cart;
      }
    };

    let added = false;
    try {
      // 1. Add the upgrade product with its contents line.
      await postJSON('cart/add.js', { items: [{ id: variantId, quantity: 1, properties }] });
      added = true;

      // 2. Take the bundle it replaces out of the cart (one unit) and get the drawer HTML back.
      const cart = await postJSON('cart/change.js', {
        id: lineKey,
        quantity: Math.max(0, lineQuantity - 1),
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      });

      if (deferred) {
        deferred.resolve({
          cart: toCart(cart),
          detail: {
            items: cart.items,
            itemCount: 1,
            sections: cart.sections,
            source: 'cart-upgrade-offer',
            didError: false,
          },
        });
      } else {
        await refreshDrawer(sectionIds, cart.sections);
      }
    } catch (error) {
      console.error(error);

      if (added) {
        // The upgrade went in but the swap failed: re-render from the live cart so the drawer is accurate.
        try {
          const cart = await fetchCart();
          if (deferred) {
            deferred.resolve({
              cart: toCart(cart),
              detail: { items: cart.items, itemCount: 1, source: 'cart-upgrade-offer', didError: true },
            });
          } else {
            await refreshDrawer(sectionIds);
          }
        } catch (fetchError) {
          deferred?.reject(fetchError);
        }
      } else {
        // Nothing changed in the cart: no re-render, keep the card and show the error.
        deferred?.reject(error);
      }

      try {
        if (CartErrorEvent) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: error instanceof Error ? error.message : 'Upgrade failed',
              code: 'INVALID',
            })
          );
        }
      } catch (eventError) {
        console.warn('[cart-upgrade] CartErrorEvent not dispatched', eventError);
      }

      if (errorElement && this.isConnected) {
        errorElement.textContent = added
          ? 'The upgrade was added, but the original bundle could not be removed. Please remove it from your cart.'
          : (error instanceof Error && error.message) || 'Could not upgrade. Please try again.';
        errorElement.classList.remove('hidden');
      }
    } finally {
      this.#busy = false;
      if (button.isConnected) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.textContent = label;
      }
    }
  }
}

if (!customElements.get('cart-upgrade-offer')) {
  customElements.define('cart-upgrade-offer', CartUpgradeOffer);
}
